import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Hero from '../components/Hero';

function Loyalty() {
  const navigate = useNavigate();
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    birthday: '',
    terms: false
  });

  useEffect(() => {
    // Animation on scroll
    const animateOnScroll = () => {
      const elements = document.querySelectorAll('.benefit-card, .tier-card');
      
      elements.forEach(element => {
        const elementPosition = element.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;
        
        if (elementPosition < windowHeight - 50) {
          element.style.opacity = '1';
          element.style.transform = 'translateY(0)';
        }
      });
    };
    
    // Set initial state for animation elements
    const elements = document.querySelectorAll('.benefit-card, .tier-card');
    elements.forEach(element => {
      element.style.opacity = '0';
      element.style.transform = 'translateY(20px)';
      element.style.transition = 'opacity 0.5s ease, transform 0.5s ease';
    });
    
    // Run animation check on load and scroll
    animateOnScroll();
    window.addEventListener('scroll', animateOnScroll);
    
    return () => {
      window.removeEventListener('scroll', animateOnScroll);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Simple validation
    if (!formData.name || !formData.email) {
      alert('Please fill out all required fields.');
      return;
    }

    if (!formData.terms) {
      alert('Please agree to receive promotional emails.');
      return;
    }
    
    // For now, just show a success message
    // In a real implementation, this would send data to a server
    setFormSubmitted(true);
  };

  return (
    <main style={{ position: 'relative' }}>
      <div className="coming-soon-overlay">
        <div className="coming-soon-content">
          <h1>COMING SOON</h1>
          <p>This page is under construction</p>
          <button onClick={() => navigate('/')} className="back-button">Back to Menu</button>
        </div>
      </div>
      <Hero 
        variant="loyalty"
        title="REWARDS PROGRAM"
        tagline='GET <span class="and">GROUNDED</span> GET REWARDED'
        showSocial={false}
      />

      <section className="loyalty-section">
        <div className="loyalty-intro">
          <h2>JOIN OUR LOYALTY PROGRAM</h2>
          <p>We believe in rewarding our regular customers. Sign up for our digital loyalty program and start earning rewards with every purchase.</p>
        </div>

        <div className="loyalty-benefits">
          <div className="benefit-card">
            <div className="benefit-icon">
              <i className="fas fa-coffee"></i>
            </div>
            <h3>Free Drinks</h3>
            <p>Earn a free drink for every 10 purchases. Any size, any style!</p>
          </div>
          <div className="benefit-card">
            <div className="benefit-icon">
              <i className="fas fa-birthday-cake"></i>
            </div>
            <h3>Birthday Reward</h3>
            <p>Receive a complimentary drink and pastry on your birthday.</p>
          </div>
          <div className="benefit-card">
            <div className="benefit-icon">
              <i className="fas fa-percent"></i>
            </div>
            <h3>Exclusive Discounts</h3>
            <p>Get early access to seasonal specials and member-only discounts.</p>
          </div>
          <div className="benefit-card">
            <div className="benefit-icon">
              <i className="fas fa-bell"></i>
            </div>
            <h3>Location Alerts</h3>
            <p>Receive notifications when we're in your favorite locations.</p>
          </div>
        </div>

        <div className="loyalty-tiers">
          <h2>LOYALTY TIERS</h2>
          <div className="tiers-container">
            <div className="tier-card">
              <h3>Bean Sprout</h3>
              <p className="tier-requirement">0-50 Points</p>
              <ul className="tier-benefits">
                <li>Earn 1 point per dollar spent</li>
                <li>Free drink on your 10th visit</li>
                <li>Birthday reward</li>
              </ul>
            </div>
            <div className="tier-card">
              <h3>Coffee Leaf</h3>
              <p className="tier-requirement">51-150 Points</p>
              <ul className="tier-benefits">
                <li>Earn 1.5 points per dollar spent</li>
                <li>Free drink on your 8th visit</li>
                <li>Birthday reward</li>
                <li>10% off merchandise</li>
              </ul>
            </div>
            <div className="tier-card featured-tier">
              <h3>Coffee Cherry</h3>
              <p className="tier-requirement">151+ Points</p>
              <ul className="tier-benefits">
                <li>Earn 2 points per dollar spent</li>
                <li>Free drink on your 5th visit</li>
                <li>Birthday reward + free pastry</li>
                <li>15% off merchandise</li>
                <li>Early access to seasonal drinks</li>
                <li>Exclusive invites to coffee tastings</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="signup-section">
        <h2>GET STARTED</h2>
        <div className="signup-container">
          <p>Signing up is easy! Just fill out this form and start earning rewards on your next visit.</p>
          {!formSubmitted ? (
            <form className="loyalty-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Full Name</label>
                <input 
                  type="text" 
                  id="name" 
                  name="name" 
                  value={formData.name}
                  onChange={handleChange}
                  required 
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input 
                  type="email" 
                  id="email" 
                  name="email" 
                  value={formData.email}
                  onChange={handleChange}
                  required 
                />
              </div>
              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <input 
                  type="tel" 
                  id="phone" 
                  name="phone" 
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="birthday">Birthday</label>
                <input 
                  type="date" 
                  id="birthday" 
                  name="birthday" 
                  value={formData.birthday}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group checkbox-group">
                <input 
                  type="checkbox" 
                  id="terms" 
                  name="terms" 
                  checked={formData.terms}
                  onChange={handleChange}
                  required 
                />
                <label htmlFor="terms">I agree to receive emails about promotions and special offers</label>
              </div>
              <button type="submit" className="cta-button">Join Now</button>
            </form>
          ) : (
            <div className="loyalty-form">
              <div className="form-success">
                <i className="fas fa-check-circle"></i>
                <h3>Thank You for Joining!</h3>
                <p>Welcome to the Grounded Coffee Co. Loyalty Program, {formData.name}! We've sent a confirmation email to {formData.email}.</p>
                <p>Your first reward will be available on your next visit.</p>
              </div>
            </div>
          )}
        </div>
        <div className="qr-signup">
          <p>Or scan this QR code at our trailer to sign up in person:</p>
          <div className="qr-placeholder">
            <img src="/images/loyalty-qr.png" alt="Loyalty Program QR Code" />
          </div>
        </div>
      </section>
    </main>
  );
}

export default Loyalty;

